import type { HelpGuide, UserRole } from '@types';
import { canUseLms, canUseServiceRequests } from '../src/common/clientAccessPolicy';
import { canManageClientAccount } from '../src/common/clientAccountAccess';
import { CLIENT_HELP_CONTENT } from './clientHelpContent';
import { HELP_CONTENT } from './helpContent';
import { INSTRUCTION_MANUAL_CONTENT, guideToManualSection } from './instructionManualContent';

export interface HelpLibraryAccess {
    fullWorkspaceAccess?: boolean;
    userRole?: UserRole;
    serviceRequestAccess?: boolean;
}

const requestTopics = new Set(['serviceRequests', 'client-request-conversation', 'client-fsat', 'client-ep-project', 'client-advance-checks', 'client-travel-security']);

export const getHelpLibraryContent = ({ fullWorkspaceAccess = false, userRole, serviceRequestAccess = false }: HelpLibraryAccess = {}): [string, HelpGuide][] => {
    const guides = Object.entries(fullWorkspaceAccess ? HELP_CONTENT : CLIENT_HELP_CONTENT).filter(([key]) => {
        if (fullWorkspaceAccess)
            return true;
        if (requestTopics.has(key))
            return canUseServiceRequests(userRole, serviceRequestAccess);
        if (key === 'subscriptionManagement' || key === 'myAccount')
            return canManageClientAccount(userRole);
        return key !== 'client-lms' || canUseLms(userRole);
    });
    const manual: HelpGuide = fullWorkspaceAccess ? { reportType: 'HelpGuide', ...INSTRUCTION_MANUAL_CONTENT } : {
        reportType: 'HelpGuide',
        title: 'IntelOptics Insights: Client Instruction Manual',
        introduction: 'Follow these steps to use your client screens, read delivered work, and get support. Topics follow the access assigned to your account.',
        sections: guides.map(([, guide]) => guideToManualSection(guide))
    };
    return [['instructionManual', manual], ...guides];
};

export const findHelpGuide = (content: [string, HelpGuide][], workflowName?: string, suiteName?: string): HelpGuide => {
    for (const name of [workflowName, suiteName]) {
        if (!name?.trim())
            continue;
        const term = name.trim().toLowerCase();
        const match = content.find(([key, guide]) => key.toLowerCase() === term || guide.title?.toLowerCase() === term)
            || content.find(([, guide]) => guide.title?.toLowerCase().includes(term));
        if (match)
            return match[1];
    }
    return content[0][1];
};

export const searchHelpContent = (content: [string, HelpGuide][], searchTerm: string): [string, HelpGuide][] => {
    const term = searchTerm.trim().toLowerCase();
    return content.filter(([, guide]) => guide.title?.toLowerCase().includes(term)
        || guide.introduction.toLowerCase().includes(term)
        || guide.sections.some(section => section.title.toLowerCase().includes(term) || section.content.toLowerCase().includes(term)));
};
